<template>
  <HgCardElevated
    :class="[$style.card, selected && $style.cardSelected]"
    @click="setAgentSelection"
  >
    <div :class="$style.cardInner">
      <HgIcon
        v-if="selected"
        icon="CheckmarkCircleSolid"
        :class="$style.verifiedIcon"
        :size="IconSize.Large"
        theme="primary"
      />
      <div :class="[$style.imageWrap, !agentImageSrc && $style.imagePlaceholder]">
        <div v-if="agentImageSrc" :class="$style.imageSquareFrame">
          <img
            :src="agentImageSrc"
            :alt="`${agent.firstName} ${agent.lastName}`"
            :class="$style.agentImage"
            loading="lazy"
          />
        </div>
        <HgIcon
          v-else
          icon="PersonOutline"
          :class="$style.placeholderIcon"
          :size="IconSize.XLarge"
          theme="current"
        />
      </div>
      <div :class="$style.content">
        <div :class="$style.header">
          <div>
            <h3 :class="$style.agentName">{{ agent.firstName }} {{ agent.lastName }}</h3>
            <p :class="$style.agencyName">{{ agent.agencyName }}</p>
          </div>
        </div>

        <p :class="$style.description">{{ agentDescription }}</p>
        <hr :class="$style.divider" />
        <div :class="$style.metaInfo">
          <span :class="[$style.metaItem, $style.metaItemNonShrink]">
            <HgIcon icon="Location" />
            <strong>{{ agent.location }}</strong>
          </span>
          <span :class="$style.metaItem">
            <HgIcon icon="Award" />
            <strong :class="$style.yearOfExp"
              >{{ agent.yearsOfExp }} {{ $t(`agentRecommendation.yearOfExp`) }}</strong
            >
          </span>
        </div>
      </div>
    </div>
  </HgCardElevated>
</template>

<script setup lang="ts">
import { AgentRecommendation } from '@/stores/types/propertyValuation';
import { HgCardElevated, HgIcon, HgSupportedLanguage, IconSize } from '@smg-real-estate/domus';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  agent: AgentRecommendation;
}>();

const { locale } = useI18n();
const currentLocale = computed(() => locale.value as HgSupportedLanguage);
const selected = ref(true);

// Recommendation payloads may not include a translation for every locale.
const agentDescription = computed(() => {
  const descriptions = props.agent.agentDescription;
  return (
    descriptions[currentLocale.value] ??
    descriptions[HgSupportedLanguage.DE] ??
    descriptions[HgSupportedLanguage.EN] ??
    Object.values(descriptions).find(Boolean) ??
    ''
  );
});

// Relative URLs resolve against the platform static assets; absolute URLs pass through.
// File names are normalized to NFD (combining marks) and spaces are replaced with `+`
// to match how the assets are stored on the media server.
const normalizeAgentImagePath = (path: string) => path.normalize('NFD').replace(/ /g, '+');
const agentImageSrc = computed(() => {
  const url = props.agent.agentImageUrl;
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return normalizeAgentImagePath(url);
  return `${import.meta.env.VUE_APP_MEDIA_BASE_URL}${normalizeAgentImagePath(url.replace(/^\/+/, ''))}`;
});

const emit = defineEmits<{
  (event: 'selectAgent', agentData: AgentRecommendation, agentSelected: boolean): void;
}>();
const setAgentSelection = (): void => {
  selected.value = !selected.value;
  emit('selectAgent', props.agent, selected.value);
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.card {
  box-sizing: border-box;
  border-radius: $space-50;
  height: 100%;
  border: $space-25 solid transparent;
  cursor: pointer;

  &[class*='HgCardElevated_cardElevated_'] > [class*='HgCardElevated_content_'] {
    height: 100%;
    padding: 0;
    border-radius: $border-radius;
  }

  @include grid-media-query(sm) {
    height: auto;
  }
}

.cardSelected {
  border: $space-25 solid $color-teal-120;
}

.cardInner {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  @include grid-media-query(sm) {
    flex-direction: row;
    height: 15.5rem; // the inner images have different ratios -> we need to apply fix height to unify of them
  }
}

.imageWrap {
  @include grid-media-query(sm) {
    flex: 0 0 43%;
  }
}

/* Square crop (aspect-ratio + object-fit) — mobile only; desktop uses full image column */
.imageSquareFrame {
  aspect-ratio: 1 / 1;

  @include grid-media-query(sm) {
    aspect-ratio: unset;
    height: 100%;
    width: 100%;
  }
}

.imagePlaceholder {
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
  background-color: $color-grey-40;

  @include grid-media-query(sm) {
    aspect-ratio: unset;
    height: 100%;
    width: 100%;
  }
}

.placeholderIcon {
  @include color-icon($color-grey-80);
}

.agentImage {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;

  @include grid-media-query(sm) {
    object-position: center;
  }
}

.content {
  display: flex;
  flex-direction: column;
  gap: $space-100;
  padding: $space-75 $space-100;
  position: relative;
  flex: 0 0 auto;
  min-height: 0;

  @include grid-media-query(sm) {
    flex: 1 1 auto;
    height: 100%;
    width: 60%;
    box-sizing: border-box;
  }
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $space-100;
}

.agentName {
  @include body-1-bold($color-grey-100);
  margin: 0;
}

.agencyName {
  @include body-3($color-grey-90);
  margin: 0;
}

.verifiedIcon {
  @include color-icon($color-teal-120);
  position: absolute;
  top: $space-25;
  right: $space-25;
  z-index: 1;
}

.description {
  @include body-3($color-grey-100);
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  min-height: 5.25rem;

  @include grid-media-query(sm) {
    -webkit-line-clamp: 5;
    min-height: 6.5rem;
  }
}

.metaInfo {
  display: flex;
  gap: $space-100;
}

.metaItem {
  @include caption-semi-bold($color-grey-90);
  display: flex;
  gap: $space-25;
}

.metaItemNonShrink {
  flex-shrink: 0;
}

.divider {
  display: block;
  width: calc(100% + (#{$space-150} * 2));
  border: none;
  border-top: 1px solid $color-grey-100;
  margin-top: auto;
  margin-right: -$space-150;
  margin-bottom: 0;
  margin-left: -$space-150;
  @include grid-media-query(sm) {
    display: none;
  }
}

.yearOfExp {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
}
</style>
